import { useInfiniteQuery } from "@tanstack/react-query";

interface HistorySummary {
  totalSent: number;
  thisMonth: number;
  thisWeek: number;
}

interface ContactBreakdown {
  contactId: string;
  contactName: string;
  suburb: string;
  reportCount: number;
  lastSentAt: string | null;
}

interface ReportEntry {
  id: string;
  contactId: string;
  contactName: string;
  suburb: string;
  sentAt: string;
  status: string;
  s3Url?: string;
}

interface HistoryResponse {
  summary: HistorySummary;
  perContact: ContactBreakdown[];
  reports: ReportEntry[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

interface UseReportHistoryParams {
  contactId?: string | null;
  startDate?: string;
  endDate?: string;
}

export function useReportHistory(params: UseReportHistoryParams = {}) {
  return useInfiniteQuery({
    queryKey: ["reports", "history", params],
    queryFn: async ({ pageParam = 0 }): Promise<HistoryResponse> => {
      const searchParams = new URLSearchParams({
        limit: "50",
        offset: String(pageParam),
      });

      if (params.contactId) {
        searchParams.set("contactId", params.contactId);
      }

      if (params.startDate) {
        searchParams.set("startDate", params.startDate);
      }

      if (params.endDate) {
        searchParams.set("endDate", params.endDate);
      }

      const response = await fetch(`/api/reports/history?${searchParams}`);
      if (!response.ok) {
        throw new Error("Failed to fetch report history");
      }
      return response.json();
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const { pagination } = lastPage;
      const nextOffset = pagination.offset + pagination.limit;
      return nextOffset < pagination.total ? nextOffset : undefined;
    },
    select: (data) => {
      // Combine pages for reports, but use first page summary/perContact
      const firstPage = data.pages[0];
      const allReports = data.pages.flatMap((page) => page.reports);
      return {
        summary: firstPage?.summary ?? {
          totalSent: 0,
          thisMonth: 0,
          thisWeek: 0,
        },
        perContact: firstPage?.perContact ?? [],
        reports: allReports,
        pagination: data.pages[data.pages.length - 1]?.pagination ?? {
          total: 0,
          limit: 50,
          offset: 0,
        },
      };
    },
  });
}
